import { useCallback, useMemo, useRef, useState } from 'react';
import { stanNotify } from '@/lib';
import {
    FileAttachmentConfig,
    FileAttachmentError,
    FileAttachmentHookReturn,
    FileAttachmentValidationResult,
} from '../types/fileAttachments.types';
import { FILE_ATTACHMENT_CONSTANTS, getFileSizeString } from '../constants/fileAttachments.constants';
import { fileAttachmentSchema } from '../validations';

export const useFileAttachments = (config: Partial<FileAttachmentConfig> = {}): FileAttachmentHookReturn => {
    const finalConfig = { ...FILE_ATTACHMENT_CONSTANTS.DEFAULT_CONFIG, ...config };
    const [attachments, setAttachments] = useState<File[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<FileAttachmentError | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const getAcceptedFileTypes = useCallback(() => {
        return finalConfig.allowedMimeTypes.join(',') + ',' + finalConfig.allowedFileTypes.join(',');
    }, [finalConfig.allowedMimeTypes, finalConfig.allowedFileTypes]);

    const validateFile = useCallback(
        (file: File): FileAttachmentValidationResult => {
            const errors: FileAttachmentError[] = [];
            const warnings: string[] = [];

            try {
                // Validate using Zod schema
                fileAttachmentSchema.parse({
                    name: file.name,
                    size: file.size,
                    type: file.type,
                    lastModified: file.lastModified,
                });
            } catch (validationError: any) {
                if (validationError.errors) {
                    validationError.errors.forEach((err: any) => {
                        errors.push({
                            type: 'unknown',
                            message: err.message,
                            file,
                            code: err.code,
                        });
                    });
                }
            }

            // Additional custom validations
            if (file.size > finalConfig.maxFileSize) {
                errors.push({
                    type: 'size',
                    message: FILE_ATTACHMENT_CONSTANTS.ERROR_MESSAGES.FILE_TOO_LARGE(
                        file.name,
                        getFileSizeString(finalConfig.maxFileSize)
                    ),
                    file,
                });
            }

            // Check MIME type, but fallback to extension check if MIME type is empty or unknown
            const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
            const isValidMimeType = finalConfig.allowedMimeTypes.includes(file.type);
            const isValidExtension = finalConfig.allowedFileTypes.includes(fileExtension);

            if (!isValidMimeType && !isValidExtension) {
                errors.push({
                    type: 'type',
                    message: FILE_ATTACHMENT_CONSTANTS.ERROR_MESSAGES.INVALID_FILE_TYPE(file.name),
                    file,
                });
            }

            // Warning for large files
            if (file.size > FILE_ATTACHMENT_CONSTANTS.FILE_SIZE_LIMITS.WARNING_SIZE) {
                warnings.push(`Large file detected: ${getFileSizeString(file.size)}`);
            }

            return {
                isValid: errors.length === 0,
                errors,
                warnings,
            };
        },
        [finalConfig.maxFileSize, finalConfig.allowedMimeTypes]
    );

    const validateFiles = useCallback(
        (files: File[]): FileAttachmentValidationResult => {
            const allErrors: FileAttachmentError[] = [];
            const allWarnings: string[] = [];

            // Check quantity limit
            if (files.length > finalConfig.maxAttachments) {
                allErrors.push({
                    type: 'quantity',
                    message: FILE_ATTACHMENT_CONSTANTS.ERROR_MESSAGES.MAX_ATTACHMENTS_REACHED(
                        finalConfig.maxAttachments
                    ),
                });
            }

            // Check total size limit
            const totalSize = files.reduce((sum, file) => sum + file.size, 0);
            if (totalSize > 50 * 1024 * 1024) {
                // 50MB total limit
                allErrors.push({
                    type: 'size',
                    message: 'Total file size exceeds 50MB limit',
                });
            }

            // Validate each file
            files.forEach(file => {
                const validation = validateFile(file);
                allErrors.push(...validation.errors);
                allWarnings.push(...validation.warnings);
            });

            return {
                isValid: allErrors.length === 0,
                errors: allErrors,
                warnings: allWarnings,
            };
        },
        [finalConfig.maxAttachments, validateFile]
    );

    const addFiles = useCallback(
        async (files: FileList | File[]): Promise<FileAttachmentValidationResult> => {
            setIsLoading(true);
            setError(null);

            try {
                const fileArray = Array.from(files);
                const validation = validateFiles(fileArray);

                if (!validation.isValid) {
                    // Show first error to user
                    const firstError = validation.errors[0];
                    if (firstError) {
                        setError(firstError);
                        stanNotify({
                            type: 'error',
                            title: 'File validation failed',
                            text: firstError.message,
                        });
                    }
                    return validation;
                }

                // Check for duplicates
                const newFiles: File[] = [];
                const duplicateErrors: FileAttachmentError[] = [];

                for (const file of fileArray) {
                    const isDuplicate = attachments.some(
                        attachment =>
                            attachment.name === file.name &&
                            attachment.size === file.size &&
                            attachment.lastModified === file.lastModified
                    );

                    if (isDuplicate) {
                        duplicateErrors.push({
                            type: 'duplicate',
                            message: FILE_ATTACHMENT_CONSTANTS.ERROR_MESSAGES.DUPLICATE_FILE(file.name),
                            file,
                        });
                    } else {
                        newFiles.push(file);
                    }
                }

                if (duplicateErrors.length > 0) {
                    const firstDuplicateError = duplicateErrors[0];
                    if (firstDuplicateError) {
                        setError(firstDuplicateError);
                    }
                }

                // Add valid files
                if (newFiles.length > 0) {
                    setAttachments(prev => [...prev, ...newFiles]);
                }

                return {
                    isValid: duplicateErrors.length === 0,
                    errors: duplicateErrors,
                    warnings: validation.warnings,
                };
            } catch (err) {
                console.error(err);
                const error: FileAttachmentError = {
                    type: 'unknown',
                    message: 'An unexpected error occurred while processing files',
                };
                setError(error);
                stanNotify({
                    type: 'error',
                    title: 'Error',
                    text: error.message,
                });
                return {
                    isValid: false,
                    errors: [error],
                    warnings: [],
                };
            } finally {
                setIsLoading(false);
            }
        },
        [attachments, validateFiles]
    );

    const removeAttachment = useCallback((index: number) => {
        setAttachments(prev => prev.filter((_, i) => i !== index));
        setError(null);
    }, []);

    const clearAttachments = useCallback(() => {
        setAttachments([]);
        setError(null);
    }, []);

    const clearError = useCallback(() => {
        setError(null);
    }, []);

    const openFileDialog = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    const handleFileSelect = useCallback(
        async (event: React.ChangeEvent<HTMLInputElement>) => {
            const files = event.target.files;
            if (files && files.length > 0) {
                await addFiles(files);
            }
            // Clear the input so the same file can be selected again
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        },
        [addFiles]
    );

    // Memoized values for performance
    const memoizedConfig = useMemo(
        () => ({
            maxAttachments: finalConfig.maxAttachments,
            maxFileSize: finalConfig.maxFileSize,
            allowedFileTypes: finalConfig.allowedFileTypes,
        }),
        [finalConfig.maxAttachments, finalConfig.maxFileSize, finalConfig.allowedFileTypes]
    );

    return {
        // State
        attachments,
        isLoading,
        error,

        // Refs
        fileInputRef,

        // Configuration
        ...memoizedConfig,

        // Actions
        addFiles,
        removeAttachment,
        clearAttachments,
        clearError,

        // UI Helpers
        openFileDialog,
        handleFileSelect,
        getAcceptedFileTypes,

        // Validation
        validateFile,
        validateFiles,
    };
};
